// CorridorArrowsMapOptimised.jsx
import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import length from "@turf/length";
import along from "@turf/along";
import { Box, Paper, Typography, Slider, Stack, Divider } from "@mui/material";

mapboxgl.accessToken =
  "pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw";

// ---------------- helpers ----------------
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function lerpCoord(c0, c1, t) {
  return [lerp(c0[0], c1[0], t), lerp(c0[1], c1[1], t)];
}
function normalizePct(x, fallback = 0.5) {
  if (x == null || Number.isNaN(Number(x))) return fallback;
  const v = Number(x);
  if (v > 1) return clamp(v / 100, 0, 1);
  return clamp(v, 0, 1);
}

// bearing in degrees from p0 -> p1
function bearingDeg(from, to) {
  const lon1 = (from[0] * Math.PI) / 180;
  const lon2 = (to[0] * Math.PI) / 180;
  const lat1 = (from[1] * Math.PI) / 180;
  const lat2 = (to[1] * Math.PI) / 180;

  const dLon = lon2 - lon1;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  let brng = (Math.atan2(y, x) * 180) / Math.PI;
  brng = (brng + 360) % 360;
  return brng;
}

// move a point by distance (meters) along bearing (degrees)
// uses simple spherical approximation (good for small offsets like 1–20m)
function offsetMeters([lng, lat], bearing, meters) {
  const R = 6378137; // earth radius (m)
  const brng = (bearing * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lng * Math.PI) / 180;

  const dByR = meters / R;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(dByR) + Math.cos(lat1) * Math.sin(dByR) * Math.cos(brng)
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(dByR) * Math.cos(lat1),
      Math.cos(dByR) - Math.sin(lat1) * Math.sin(lat2)
    );

  return [(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
}

// SDF arrow icon (points UP by default)
function makeArrowCanvas(size = 64) {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");

  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";

  // slim arrow
  ctx.beginPath();
  ctx.moveTo(size * 0.5, size * 0.06);
  ctx.lineTo(size * 0.78, size * 0.92);
  ctx.lineTo(size * 0.5, size * 0.76);
  ctx.lineTo(size * 0.22, size * 0.92);
  ctx.closePath();
  ctx.fill();

  return ctx.getImageData(0, 0, size, size);;
}

// sample a line into N points at equal distance
function sampleLine(lineFeature, sampleCount) {
  const km = length(lineFeature, { units: "kilometers" });
  const totalMeters = Math.max(1, km * 1000);
  const samples = [];
  for (let i = 0; i < sampleCount; i++) {
    const t01 = sampleCount === 1 ? 0 : i / (sampleCount - 1);
    const distKm = (t01 * totalMeters) / 1000;
    const pt = along(lineFeature, distKm, { units: "kilometers" });
    samples.push(pt.geometry.coordinates);
  }
  return { totalMeters, samples };
}

function buildCorridorCache(corridors, sampleCount = 260) {
  return corridors.map((c) => {
    const L = sampleLine(c.leftBoundary, sampleCount);
    const R = sampleLine(c.rightBoundary, sampleCount);
    const corridorMeters = (L.totalMeters + R.totalMeters) / 2;

    const throughputA = Number(c.throughputA ?? 0) || 0;
    const throughputB = Number(c.throughputB ?? 0) || 0;

    const congestionA = normalizePct(c.congestionA, 0.5);
    const congestionB = normalizePct(c.congestionB, 0.5);

    return {
      id: c.id,
      colorA: c.colorA ?? c.color ?? "#4FD1C5",
      colorB: c.colorB ?? "#63B3ED",
      throughputA,
      throughputB,
      congestionA,
      congestionB,
      leftSamples: L.samples,
      rightSamples: R.samples,
      corridorMeters,
      sampleCount,
      evalLR(s01) {
        const s = clamp(s01, 0, 1);
        const x = s * (sampleCount - 1);
        const i0 = Math.floor(x);
        const i1 = Math.min(sampleCount - 1, i0 + 1);
        const t = x - i0;
        const L0 = this.leftSamples[i0];
        const L1 = this.leftSamples[i1];
        const R0 = this.rightSamples[i0];
        const R1 = this.rightSamples[i1];
        return { L: lerpCoord(L0, L1, t), R: lerpCoord(R0, R1, t) };
      },
      evalPoint(s01, u01) {
        const { L, R } = this.evalLR(s01);
        return lerpCoord(L, R, clamp(u01, 0, 1));
      },
    };
  });
}

function sampleCenterBiasedU(centerBias) {
  const uUniform = Math.random();
  if (centerBias <= 0) return uUniform;

  const r1 = Math.random();
  const r2 = Math.random();
  const tri = 0.5 + (r1 - r2) * 0.45;
  const uTri = clamp(tri, 0, 1);

  return lerp(uUniform, uTri, clamp(centerBias, 0, 1));
}

function pickWeightedIndex(weights) {
  let sum = 0;
  for (const w of weights) sum += w;
  if (sum <= 0) return Math.floor(Math.random() * weights.length);

  let r = Math.random() * sum;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r <= 0) return i;
  }
  return weights.length - 1;
}

// ---------------- DEMO corridor ----------------
export const DEMO_CORRIDORS = [
  {
    id: "funnel-demo",
    colorA: "#4FD1C5",
    colorB: "#63B3ED",
    throughputA: 18,
    throughputB: 10,
    congestionA: 0.6,
    congestionB: 0.35,
    leftBoundary: {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [151.0704, -33.8461],
          [151.07005, -33.84635],
          [151.06975, -33.8466],
          [151.0695, -33.84685],
          [151.0693, -33.8471],
          [151.0691, -33.84735],
        ],
      },
    },
    rightBoundary: {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [151.07105, -33.84625],
          [151.07055, -33.84648],
          [151.0701, -33.8467],
          [151.06972, -33.84692],
          [151.06965, -33.84715],
          [151.06975, -33.84745],
        ],
      },
    },
  },
];

// ---------------- Component ----------------
export default function CorridorArrowsMapOptimised({
  corridors = DEMO_CORRIDORS,
  initialCenter = [151.07, -33.84635],
  prefill = true,
  animate = true,
  corridorChangeDelayMs = 500,

  capPer100m = 220,

  initialArrowSize = 0.32,

  // Glow + streak tuning
  glowMultiplier = 1.55,
  glowOpacity = 0.22,
  coreOpacity = 0.95,

  // streak (thin line under arrow)
  streakMeters = 10, // length of the streak in meters
  streakWidth = 2.0, // px
  streakOpacity = 0.45, // multiplier (times edgeFade)
  streakGlowWidth = 5.5, // px
  streakGlowOpacity = 0.18,

  renderEveryMs = 0,
}) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  const rafRef = useRef(null);
  const lastTRef = useRef(null);
  const lastRenderTRef = useRef(0);

  const corridorsPropRef = useRef(corridors);
  useEffect(() => void (corridorsPropRef.current = corridors), [corridors]);

  const animateRef = useRef(animate);
  useEffect(() => void (animateRef.current = animate), [animate]);

  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);

  const cacheRef = useRef([]);
  const idRef = useRef(1);
  const objectsRef = useRef([]);
  const spawnAccRef = useRef({ a: 0, b: 0 });

  // UI controls
  const [arrowSize, setArrowSize] = useState(initialArrowSize);
  const [centerBias, setCenterBias] = useState(0.65);
  const [crossDrift, setCrossDrift] = useState(0.02);
  const [speedA, setSpeedA] = useState(3.6);
  const [speedB, setSpeedB] = useState(2.8);

  // stable per-instance icon id
  const arrowIconIdRef = useRef(null);
  if (!arrowIconIdRef.current) {
    arrowIconIdRef.current = `flow-arrow-64-${Math.random().toString(16).slice(2)}`;
  }

  const TANGENT_EPS = 0.0025;

  const computeCaps = () => {
    const cache = cacheRef.current;
    const capsA = new Array(cache.length).fill(0);
    const capsB = new Array(cache.length).fill(0);
    for (let i = 0; i < cache.length; i++) {
      const c = cache[i];
      const base = (c.corridorMeters / 100) * capPer100m;
      capsA[i] = Math.max(0, Math.floor(base * c.congestionA));
      capsB[i] = Math.max(0, Math.floor(base * c.congestionB));
    }
    return { capsA, capsB };
  };

  // Build BOTH: arrows (points) and streaks (lines) in one pass
  const buildFeatureCollections = () => {
    const cache = cacheRef.current;

    const arrowFeatures = [];
    const streakFeatures = [];

    for (const o of objectsRef.current) {
      const corridor = cache[o.corridorIdx];
      if (!corridor) continue;

      const s = o.s;
      const u = o.u;

      const p0 = corridor.evalPoint(s, u);

      const s1 =
        o.dir === 1
          ? clamp(s + TANGENT_EPS, 0, 1)
          : clamp(s - TANGENT_EPS, 0, 1);
      const p1 = corridor.evalPoint(s1, u);

      const b = bearingDeg(p0, p1);
      const edgeFade = clamp(Math.min(s, 1 - s) * 8, 0, 1);
      const col = o.dir === 1 ? corridor.colorA : corridor.colorB;

      // streak behind arrow: from "tail" to "head" (near p0)
      const tail = offsetMeters(p0, (b + 180) % 360, streakMeters);

      streakFeatures.push({
        type: "Feature",
        properties: { color: col, a: edgeFade },
        geometry: {
          type: "LineString",
          coordinates: [tail, p0],
        },
      });

      arrowFeatures.push({
        type: "Feature",
        properties: {
          color: col,
          a: edgeFade,
          bearing: b,
          size: arrowSize,
          glowSize: arrowSize * glowMultiplier,
        },
        geometry: { type: "Point", coordinates: p0 },
      });
    }

    return {
      arrows: { type: "FeatureCollection", features: arrowFeatures },
      streaks: { type: "FeatureCollection", features: streakFeatures },
    };
  };

  const doPrefill = () => {
    const cache = cacheRef.current;
    if (!cache.length) {
      objectsRef.current = [];
      return;
    }

    const { capsA, capsB } = computeCaps();
    const objs = [];

    const addObj = (dir, corridorIdx) => {
      const u = sampleCenterBiasedU(centerBias);
      const drift = (Math.random() * 2 - 1) * 0.22;
      const speedMult = 0.7 + Math.random() * 0.95;
      const s = Math.random();
      objs.push({ id: idRef.current++, corridorIdx, s, u, dir, speedMult, drift });
    };

    for (let i = 0; i < cache.length; i++) {
      for (let k = 0; k < capsA[i]; k++) addObj(1, i);
      for (let k = 0; k < capsB[i]; k++) addObj(-1, i);
    }

    objectsRef.current = objs;
    spawnAccRef.current = { a: 0, b: 0 };
  };

  const pushRenderNow = () => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const { arrows, streaks } = buildFeatureCollections();

    const srcA = map.getSource("flow-arrows");
    if (srcA) srcA.setData(arrows);

    const srcS = map.getSource("flow-streaks");
    if (srcS) srcS.setData(streaks);
  };

  const applyCorridorsUpdate = () => {
    pausedRef.current = true;

    cacheRef.current = buildCorridorCache(corridorsPropRef.current, 240);

    const map = mapRef.current;
    if (map && map.isStyleLoaded() && map.getSource("corridor-boundaries")) {
      const boundaryFC = {
        type: "FeatureCollection",
        features: corridorsPropRef.current.flatMap((c) => [
          { ...c.leftBoundary, properties: { ...(c.leftBoundary.properties || {}), corridor: c.id, side: "left" } },
          { ...c.rightBoundary, properties: { ...(c.rightBoundary.properties || {}), corridor: c.id, side: "right" } },
        ]),
      };
      map.getSource("corridor-boundaries").setData(boundaryFC);
    }

    if (prefill) doPrefill();
    else objectsRef.current = [];

    pushRenderNow();

    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, corridorChangeDelayMs);
  };

  // create map once
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: initialCenter,
      zoom: 15.8,
      pitch: 45,
      bearing: -20,
      antialias: true,
    });

    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.on("load", () => {
      map.addSource("corridor-boundaries", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addSource("flow-streaks", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addSource("flow-arrows", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      // boundaries
      map.addLayer({
        id: "corridor-boundaries-layer",
        type: "line",
        source: "corridor-boundaries",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#A0AEC0", "line-opacity": 0.18, "line-width": 3 },
      });

      // STREAK GLOW (behind everything)
      map.addLayer({
        id: "flow-streaks-glow",
        type: "line",
        source: "flow-streaks",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": streakGlowWidth,
          "line-opacity": ["*", ["get", "a"], streakGlowOpacity],
          "line-blur": 1.2,
        },
      });

      // STREAK CORE
      map.addLayer({
        id: "flow-streaks-core",
        type: "line",
        source: "flow-streaks",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": streakWidth,
          "line-opacity": ["*", ["get", "a"], streakOpacity],
          "line-blur": 0.2,
        },
      });

      // arrow icon
      const ARROW_ICON_ID = arrowIconIdRef.current;
      if (!map.hasImage(ARROW_ICON_ID)) {
        map.addImage(ARROW_ICON_ID, makeArrowCanvas(64), { sdf: true });
      }

      // arrow glow (symbol)
      map.addLayer({
        id: "flow-arrows-glow",
        type: "symbol",
        source: "flow-arrows",
        layout: {
          "icon-image": ARROW_ICON_ID,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-size": ["get", "glowSize"],
          "icon-rotate": ["get", "bearing"],
          "icon-rotation-alignment": "map",
        },
        paint: {
          "icon-color": ["get", "color"],
          "icon-opacity": ["*", ["get", "a"], glowOpacity],
        },
      });

      // arrow core
      map.addLayer({
        id: "flow-arrows-core",
        type: "symbol",
        source: "flow-arrows",
        layout: {
          "icon-image": ARROW_ICON_ID,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-size": ["get", "size"],
          "icon-rotate": ["get", "bearing"],
          "icon-rotation-alignment": "map",
        },
        paint: {
          "icon-color": ["get", "color"],
          "icon-opacity": ["*", ["get", "a"], coreOpacity],
        },
      });

      applyCorridorsUpdate();

      const tick = (ts) => {
        if (!lastTRef.current) lastTRef.current = ts;
        const dt = (ts - lastTRef.current) / 1000;
        lastTRef.current = ts;

        if (!animateRef.current || pausedRef.current) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }

        const cache = cacheRef.current;
        if (!cache.length) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }

        const { capsA, capsB } = computeCaps();

        const countA = new Array(cache.length).fill(0);
        const countB = new Array(cache.length).fill(0);
        for (const o of objectsRef.current) {
          if (o.dir === 1) countA[o.corridorIdx] = (countA[o.corridorIdx] ?? 0) + 1;
          else countB[o.corridorIdx] = (countB[o.corridorIdx] ?? 0) + 1;
        }

        let totalThroughA = 0;
        let totalThroughB = 0;
        const weightsA = new Array(cache.length).fill(0);
        const weightsB = new Array(cache.length).fill(0);

        for (let i = 0; i < cache.length; i++) {
          weightsA[i] = Math.max(0, cache[i].throughputA);
          weightsB[i] = Math.max(0, cache[i].throughputB);
          totalThroughA += weightsA[i];
          totalThroughB += weightsB[i];
        }

        const acc = spawnAccRef.current;
        acc.a += totalThroughA * dt;
        acc.b += totalThroughB * dt;

        const spawnOne = (dir, corridorIdx) => {
          const u = sampleCenterBiasedU(centerBias);
          const drift = (Math.random() * 2 - 1) * 0.22;
          const speedMult = 0.7 + Math.random() * 0.95;
          const s = dir === 1 ? Math.random() * 0.03 : 1 - Math.random() * 0.03;
          objectsRef.current.push({ id: idRef.current++, corridorIdx, s, u, dir, speedMult, drift });
        };

        while (acc.a >= 1 && totalThroughA > 0) {
          const idx = pickWeightedIndex(weightsA);
          if (countA[idx] < capsA[idx]) {
            spawnOne(1, idx);
            countA[idx]++;
          }
          acc.a -= 1;
        }

        while (acc.b >= 1 && totalThroughB > 0) {
          const idx = pickWeightedIndex(weightsB);
          if (countB[idx] < capsB[idx]) {
            spawnOne(-1, idx);
            countB[idx]++;
          }
          acc.b -= 1;
        }

        const next = [];
        for (const o of objectsRef.current) {
          const corridor = cache[o.corridorIdx];
          if (!corridor) continue;

          const metersPerSec = (o.dir === 1 ? speedA : speedB) * o.speedMult;
          const ds = (metersPerSec * dt) / corridor.corridorMeters;
          o.s += o.dir * ds;

          o.u += o.drift * crossDrift * dt * 4.0;
          if (o.u < 0) {
            o.u = -o.u;
            o.drift *= -1;
          }
          if (o.u > 1) {
            o.u = 2 - o.u;
            o.drift *= -1;
          }

          if (o.s < 0 || o.s > 1) continue;
          next.push(o);
        }
        objectsRef.current = next;

        const shouldRender = renderEveryMs <= 0 || ts - lastRenderTRef.current >= renderEveryMs;
        if (shouldRender) {
          lastRenderTRef.current = ts;
          pushRenderNow();
        }

        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    });

    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (mapRef.current) mapRef.current.remove();
      mapRef.current = null;
      lastTRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // map once

  useEffect(() => {
    if (!mapRef.current) return;
    applyCorridorsUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corridors]);

  useEffect(() => {
    pushRenderNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrowSize]);

  useEffect(() => {
    if (!mapRef.current) return;
    applyCorridorsUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // ---------------- Soft UI panel ----------------
  const softPanelSx = {
    width: 430,
    borderRadius: "18px",
    p: 2,
    background: "rgba(255,255,255,0.78)",
    backdropFilter: "blur(14px)",
    border: "1px solid rgba(255,255,255,0.55)",
    boxShadow: "0 20px 40px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.35)",
  };
  const labelSx = { fontSize: 12.5, opacity: 0.8 };
  const valueSx = { fontWeight: 800 };

  const LabeledSlider = ({ label, value, fmt, min, max, step, onChange }) => (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography sx={labelSx}>{label}</Typography>
        <Typography sx={valueSx}>{fmt ? fmt(value) : value}</Typography>
      </Stack>
      <Slider value={value} min={min} max={max} step={step} onChange={(_, v) => onChange(v)} />
    </Box>
  );

  return (
    <Box sx={{ position: "relative", height: "100vh", width: "100%" }}>
      <Box ref={mapContainerRef} sx={{ position: "absolute", inset: 0 }} />

      <Paper elevation={0} sx={{ ...softPanelSx, position: "absolute", left: 14, top: 14 }}>
        <Stack spacing={1.2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              Two-way corridor arrows + streaks
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: 0.75 }}>
              prefill={String(prefill)} • animate={String(animate)} • resume delay={corridorChangeDelayMs}ms
            </Typography>
          </Box>

          <Divider sx={{ opacity: 0.35 }} />

          <Typography sx={{ fontWeight: 900, fontSize: 13, opacity: 0.8 }}>Look</Typography>
          <LabeledSlider
            label="Arrow size"
            value={arrowSize}
            fmt={(v) => v.toFixed(2)}
            min={0.12}
            max={1.2}
            step={0.01}
            onChange={setArrowSize}
          />
          <LabeledSlider
            label="Center bias"
            value={centerBias}
            fmt={(v) => v.toFixed(2)}
            min={0}
            max={1}
            step={0.01}
            onChange={setCenterBias}
          />
          <LabeledSlider
            label="Cross drift"
            value={crossDrift}
            fmt={(v) => v.toFixed(3)}
            min={0}
            max={0.1}
            step={0.001}
            onChange={setCrossDrift}
          />

          <Divider sx={{ opacity: 0.35 }} />

          <Typography sx={{ fontWeight: 900, fontSize: 13, opacity: 0.8 }}>Movement (global)</Typography>
          <LabeledSlider
            label="Speed A (m/s)"
            value={speedA}
            fmt={(v) => v.toFixed(1)}
            min={0.2}
            max={25}
            step={0.1}
            onChange={setSpeedA}
          />
          <LabeledSlider
            label="Speed B (m/s)"
            value={speedB}
            fmt={(v) => v.toFixed(1)}
            min={0.2}
            max={25}
            step={0.1}
            onChange={setSpeedB}
          />

          <Typography sx={{ fontSize: 12, opacity: 0.75, lineHeight: 1.35 }}>
            Per-corridor controls live in <b>corridors</b>:
            <br />• <b>throughputA/B</b> = arrows/sec
            <br />• <b>congestionA/B</b> = % (0..1 or 0..100) → cap via <b>{capPer100m}</b>/100m
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
